import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Calculator, MapPin, FileText, Calendar, X, ChevronLeft, ChevronRight, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ADUFeatureBox } from "@/components/ADUFeatureBox";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

// Declare Calendly types for TypeScript
declare global {
  interface Window {
    Calendly: {
      initPopupWidget: (options: {
        url: string;
      }) => void;
    };
  }
}
interface CalculatorFormData {
  aduType: string;
  county: string;
  city: string;
  purposes: string[];
  otherPurpose: string;
  designChoice: string;
  squareFootage: string;
  finishLevel: string;
  firstName: string;
  lastName: string;
  propertyAddress: string;
  email: string;
  phone: string;
}
const UDAADUInsider = () => {
  const [activeFeature, setActiveFeature] = useState("overview");
  const [selectedCity, setSelectedCity] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [sentVerificationCode, setSentVerificationCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const isMobile = useIsMobile();
  const {
    toast
  } = useToast();
  const [calculatorFormData, setCalculatorFormData] = useState<CalculatorFormData>({
    aduType: "",
    county: "",
    city: "",
    purposes: [],
    otherPurpose: "",
    designChoice: "",
    squareFootage: "",
    finishLevel: "",
    firstName: "",
    lastName: "",
    propertyAddress: "",
    email: "",
    phone: ""
  });

  // Fetch cities for regulations dropdown
  const {
    data: cities
  } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('cities').select('*').order('name');
      if (error) throw error;
      return data;
    }
  });

  // Fetch city regulations
  const {
    data: regulations,
    isLoading: regulationsLoading
  } = useQuery({
    queryKey: ['city_regulations', selectedCity],
    queryFn: async () => {
      if (!selectedCity) return [];
      const {
        data,
        error
      } = await supabase.from('city_regulations').select('*').eq('city_id', selectedCity);
      if (error) throw error;
      return data;
    },
    enabled: !!selectedCity
  });
  const features = [{
    id: "overview",
    title: "ADU Overview",
    icon: <Home />,
    description: "Learn the basics of Accessory Dwelling Units"
  }, {
    id: "calculator",
    title: "Cost Calculator",
    icon: <Calculator />,
    description: "Estimate your ADU project costs"
  }, {
    id: "regulations",
    title: "Local Regulations",
    icon: <MapPin />,
    description: "City-specific ADU requirements"
  }, {
    id: "financing",
    title: "Financing",
    icon: <DollarSign />,
    description: "Loan options and financial benefits"
  }, {
    id: "permits",
    title: "Our Process",
    icon: <FileText />,
    description: "Navigate our ADU process"
  }, {
    id: "consultation",
    title: "Free Consultation",
    icon: <Calendar />,
    description: "Book a call with our experts"
  }];
  const processSteps = ["Book a Free Consultation — Schedule right from our site", "Zoom Intro Call — Discuss budget, layout, and goals", "On-Site Meeting — Walk the site with our experts", "Custom ADU Proposal — Transparent, itemized quote", "Agreement Signed — Kickoff begins", "Design + Permits — We handle it all, A to Z", "Permit Approval — We manage all city/county submissions", "Construction Phase — From trench to trim", "Welcome Your ADU — A move-in-ready space, done right"];
  const openCalendlyModal = () => {
    console.log('Opening Calendly modal');
    setShowCalendly(true);
  };
  const closeCalendlyModal = () => {
    console.log('Closing Calendly modal');
    setShowCalendly(false);
  };
  const handleBookConsultation = () => {
    openCalendlyModal();
  };
  const renderCalculatorStep = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Type of ADU</h2>
            <div className="grid gap-3">
              {aduTypes.map(type => <label key={type} className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-accent">
                  <input type="radio" name="aduType" value={type} checked={calculatorFormData.aduType === type} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                aduType: e.target.value
              }))} className="w-4 h-4 text-[#e4683b]" />
                  <span className="text-sm md:text-base">{type}</span>
                </label>)}
            </div>
          </div>;
      case 2:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Select Your County and City</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="county">County</Label>
                <Select value={calculatorFormData.county} onValueChange={value => setCalculatorFormData(prev => ({
                ...prev,
                county: value,
                city: ""
              }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select County" />
                  </SelectTrigger>
                  <SelectContent>
                    {counties.map(county => <SelectItem key={county} value={county}>{county}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              {calculatorFormData.county && <div>
                  <Label htmlFor="city">City</Label>
                  <Select value={calculatorFormData.city} onValueChange={value => setCalculatorFormData(prev => ({
                ...prev,
                city: value
              }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                    <SelectContent>
                      {calculatorCities[calculatorFormData.county as keyof typeof calculatorCities]?.tier1.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                      {calculatorCities[calculatorFormData.county as keyof typeof calculatorCities]?.tier2.map(city => <SelectItem key={city} value={city}>{city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>}
            </div>
          </div>;
      case 3:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Purpose of ADU</h2>
            <div className="space-y-3">
              {purposeOptions.map(purpose => <div key={purpose} className="flex items-center space-x-2">
                  <Checkbox id={purpose} checked={calculatorFormData.purposes.includes(purpose)} onCheckedChange={checked => handlePurposeChange(purpose, checked as boolean)} />
                  <Label htmlFor={purpose} className="text-sm md:text-base cursor-pointer">{purpose}</Label>
                </div>)}
              <div className="flex items-center space-x-2">
                <Checkbox id="other" checked={calculatorFormData.purposes.includes("Other")} onCheckedChange={checked => {
                if (checked) {
                  setCalculatorFormData(prev => ({
                    ...prev,
                    purposes: [...prev.purposes, "Other"]
                  }));
                } else {
                  setCalculatorFormData(prev => ({
                    ...prev,
                    purposes: prev.purposes.filter(p => p !== "Other"),
                    otherPurpose: ""
                  }));
                }
              }} />
                <Label htmlFor="other" className="text-sm md:text-base cursor-pointer">Other</Label>
              </div>
              {calculatorFormData.purposes.includes("Other") && <Input placeholder="Please specify..." value={calculatorFormData.otherPurpose} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              otherPurpose: e.target.value
            }))} className="mt-2" />}
            </div>
          </div>;
      case 4:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Design & Permitting</h2>
            <div className="grid gap-3">
              {designOptions.map(option => <label key={option} className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-accent">
                  <input type="radio" name="designChoice" value={option} checked={calculatorFormData.designChoice === option} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                designChoice: e.target.value
              }))} className="w-4 h-4 text-[#e4683b]" />
                  <span className="text-sm md:text-base">{option}</span>
                </label>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Square Footage and Finish Level</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="squareFootage">Square Footage</Label>
                <Input id="squareFootage" type="number" placeholder="Enter square footage" value={calculatorFormData.squareFootage} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                squareFootage: e.target.value
              }))} />
              </div>
              
              <div>
                <Label htmlFor="finishLevel">Finish Material Category</Label>
                <Select value={calculatorFormData.finishLevel} onValueChange={value => setCalculatorFormData(prev => ({
                ...prev,
                finishLevel: value
              }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select finish level" />
                  </SelectTrigger>
                  <SelectContent>
                    {finishLevels.map(level => <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>;
      case 6:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" value={calculatorFormData.firstName} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                firstName: e.target.value
              }))} required />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input id="lastName" value={calculatorFormData.lastName} onChange={e => setCalculatorFormData(prev => ({
                ...prev,
                lastName: e.target.value
              }))} required />
              </div>
            </div>
            <div>
              <Label htmlFor="propertyAddress">Property Address *</Label>
              <Input id="propertyAddress" value={calculatorFormData.propertyAddress} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              propertyAddress: e.target.value
            }))} required />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input id="email" type="email" value={calculatorFormData.email} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              email: e.target.value
            }))} required />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input id="phone" type="tel" value={calculatorFormData.phone} onChange={e => setCalculatorFormData(prev => ({
              ...prev,
              phone: e.target.value
            }))} required />
            </div>
          </div>;
      case 7:
        return <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Phone Verification</h2>
            {!isCodeSent ? <div className="text-center space-y-4">
                <p className="text-muted-foreground">
                  We'll send a verification code to {calculatorFormData.phone} to verify your phone number.
                </p>
              </div> : <div className="space-y-4">
                <p className="text-center text-muted-foreground">
                  Enter the 6-digit verification code sent to {calculatorFormData.phone}
                </p>
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="text-center">
                  <Button variant="link" onClick={() => {
                setIsCodeSent(false);
                setVerificationCode("");
                setSentVerificationCode("");
              }} disabled={isSendingCode}>
                    Didn't receive the code? Send again
                  </Button>
                </div>
              </div>}
          </div>;
      default:
        return null;
    }
  };
  const renderFeatureContent = () => {
    switch (activeFeature) {
      case "overview":
        return <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">What is an ADU?</h2>
            <p className="text-lg text-gray-600">
              An Accessory Dwelling Unit (ADU) is a secondary housing unit on a single-family 
              residential lot. ADUs can be attached to or detached from the main house.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Types of ADUs</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li>• Detached ADUs</li>
                    <li>• Attached ADUs</li>
                    <li>• Garage Conversions</li>
                    <li>• Basement Conversions</li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Benefits</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li>• Additional rental income</li>
                    <li>• Increased property value</li>
                    <li>• Housing for family</li>
                    <li>• Flexible living space</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>;
      case "calculator":
        return <div className="space-y-6">
            <h2 className="font-bold text-gray-800 text-2xl mx-[22px]">ADU Cost Calculator</h2>
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="flex items-center justify-center gap-2 md:text-3xl text-xl">
                  <Calculator className="h-6 w-6 md:h-8 md:w-8 text-[#e4683b]" />
                  UDA ADU Calculator
                </CardTitle>
                <div className="flex justify-center mt-4">
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5, 6, 7].map(step => <div key={step} className={`w-3 h-3 rounded-full ${step === currentStep ? "bg-[#e4683b]" : step < currentStep ? "bg-[#e4683b]/60" : "bg-gray-300"}`} />)}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Step {currentStep} of 7</p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {renderCalculatorStep()}
                
                <div className="flex justify-between pt-6">
                  <Button variant="secondary" onClick={handleBack} disabled={currentStep <= 6} className="flex items-center gap-2 bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400">
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button onClick={handleNext} disabled={!isStepValid() || isSendingCode} className="flex items-center gap-2 bg-[#e4683b] hover:bg-[#e4683b]/80">
                    {currentStep === 7 ? !isCodeSent ? isSendingCode ? "Sending..." : "Send Verification Code" : "Verify & View Price" : "Next"}
                    {currentStep < 7 && <ChevronRight className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>;
      case "regulations":
        return <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">Local ADU Regulations</h2>
            <div className="space-y-4">
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your city" />
                </SelectTrigger>
                <SelectContent>
                  {cities?.map(city => <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              
              {regulationsLoading && <p>Loading regulations...</p>}
              
              {regulations && regulations.length > 0 && <div className="space-y-4">
                  {regulations.map(regulation => <Card key={regulation.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{regulation.category}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="mb-2">{regulation.description}</p>
                        <p className="text-sm text-gray-600">{regulation.details}</p>
                      </CardContent>
                    </Card>)}
                </div>}
            </div>
          </div>;
      case "financing":
        return <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">ADU Financing Options</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-[#e4683b]">Loan Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Home Equity Loans (HELOCs)</h4>
                    <p className="text-gray-600 text-sm">Borrow against home equity with flexible repayment.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Cash-Out Refinancing</h4>
                    <p className="text-gray-600 text-sm">Refinance your mortgage to fund construction.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Construction Loans</h4>
                    <p className="text-gray-600 text-sm">Short-term loans for ADU projects.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">ADU Grant Programs</h4>
                    <p className="text-gray-600 text-sm">Government funding and incentives.</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-[#e4683b]">Financial Benefits</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">ROI Analysis</h4>
                    <p className="text-gray-600 text-sm">Understand potential returns before building.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Monthly Payment Estimates</h4>
                    <p className="text-gray-600 text-sm">Breakdown of financing costs.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Rental Income Potential</h4>
                    <p className="text-gray-600 text-sm">Estimated earnings from rental opportunities.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Property Value Increase</h4>
                    <p className="text-gray-600 text-sm">ADUs can add long-term value to your home.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="mt-6">
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Ready to Explore Your Financing Options?</h3>
                  <p className="text-gray-600">Schedule a consultation to discuss the best financing strategy for your ADU project.</p>
                  <Button className="bg-[#e4683b] hover:bg-[#e4683b]/80" onClick={handleBookConsultation}>
                    Discuss Financing Options
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>;
      case "permits":
        return <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">Our Process</h2>
            <Card>
              <CardHeader className="md:p-6 p-4">
                <CardTitle className="flex items-center gap-2 text-2xl md:text-3xl text-center">
                  <Calendar className="h-6 w-6 md:h-8 md:w-8 text-[#e4683b]" />
                  Our Process
                </CardTitle>
              </CardHeader>
              <CardContent className="md:p-6 p-4 pt-0">
                <div className="space-y-3 md:space-y-4">
                  {processSteps.map((step, index) => <div key={index} className="flex items-start gap-3 md:gap-4">
                      <div className="bg-[#e4683b] text-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-xs md:text-sm font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-gray-700 leading-relaxed text-sm md:text-base">{step}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>;
      case "consultation":
        return <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">Book Free Consultation</h2>
            <Card>
              <CardHeader>
                <CardTitle>Expert ADU Guidance</CardTitle>
                <CardDescription>Schedule a free 30-minute consultation </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p>Our experts can help you with:</p>
                  <ul className="space-y-2 ml-4">
                    <li>• Feasibility assessment</li>
                    <li>• Design recommendations</li>
                    <li>• Permit guidance</li>
                    <li>• Cost estimation</li>
                    <li>• Contractor connections</li>
                  </ul>
                  <Button onClick={handleBookConsultation} className="w-full bg-[#e4683b] hover:bg-[#e4683b]/80 text-3xl font-light">
                    Book Your Free Consultation
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>;
      default:
        return <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">Feature Coming Soon</h2>
            <p className="text-lg text-gray-600">
              This feature is currently under development. Check back soon!
            </p>
          </div>;
    }
  };

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);
  const counties = ["San Mateo County", "Santa Clara County"];
  const calculatorCities = {
    "San Mateo County": {
      tier1: ["Atherton", "Belmont", "Hillsborough", "Los Altos", "Los Altos Hills", "Menlo Park", "Palo Alto", "Portola Valley", "Woodside"],
      tier2: ["City of San Mateo", "County of San Mateo", "Redwood City", "San Carlos"]
    },
    "Santa Clara County": {
      tier1: ["Los Gatos", "Monte Sereno"],
      tier2: ["Campbell", "City of Santa Clara", "County of Santa Clara", "Cupertino", "San Jose", "Saratoga", "Sunnyvale", "Mountain View"]
    }
  };
  const aduTypes = ["Detached ADU", "Attached ADU", "Junior ADU (JADU)", "Garage Conversion", "Basement Conversion"];
  const purposeOptions = ["Additional income / rental property", "Housing for elderly parents", "Housing for young adults", "Guest house", "Home office / studio", "Future downsizing / personal use"];
  const designOptions = ["I already have approved plans/permits", "I'm working with an architect", "I'd like a fully custom ADU from UDA", "I'd prefer to choose from UDA's pre-designed plans", "Not sure yet"];
  const finishLevels = [{
    value: "standard",
    label: "Standard",
    cost: 99
  }, {
    value: "elite",
    label: "Elite",
    cost: 198
  }, {
    value: "deluxe",
    label: "Deluxe",
    cost: 297
  }];

  // Calculator functions
  const calculateCost = () => {
    const sf = parseInt(calculatorFormData.squareFootage);
    let designBuildRate = 0;
    const isTier1 = calculatorCities[calculatorFormData.county as keyof typeof calculatorCities]?.tier1.includes(calculatorFormData.city);
    if (sf < 400) {
      designBuildRate = isTier1 ? 498 : 448;
    } else if (sf <= 800) {
      designBuildRate = isTier1 ? 488 : 438;
    } else {
      designBuildRate = isTier1 ? 478 : 428;
    }
    const finishLevel = finishLevels.find(level => level.value === calculatorFormData.finishLevel);
    const materialRate = finishLevel?.cost || 99;
    const totalCost = (designBuildRate + materialRate) * sf;
    setCalculatedCost(totalCost);
  };
  const sendVerificationCode = async () => {
    setIsSendingCode(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('send-verification-sms', {
        body: {
          phoneNumber: calculatorFormData.phone
        }
      });
      if (error) throw error;
      if (data.success) {
        setSentVerificationCode(data.verificationCode);
        setIsCodeSent(true);
        toast({
          title: "Verification code sent",
          description: `A 6-digit code has been sent to ${calculatorFormData.phone}`
        });
      } else {
        throw new Error(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast({
        title: "Error",
        description: "Failed to send verification code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSendingCode(false);
    }
  };
  const verifyCode = () => {
    if (verificationCode === sentVerificationCode) {
      setIsVerified(true);
      calculateCost();
      setShowResult(true);
      toast({
        title: "Phone verified successfully",
        description: "Your phone number has been verified!"
      });
    } else {
      toast({
        title: "Invalid code",
        description: "The verification code you entered is incorrect. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleNext = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    } else {
      if (!isCodeSent) {
        sendVerificationCode();
      } else {
        verifyCode();
      }
    }
  };
  const handleBack = () => {
    if (currentStep === 7 && currentStep > 6) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handlePurposeChange = (purpose: string, checked: boolean) => {
    if (checked) {
      setCalculatorFormData(prev => ({
        ...prev,
        purposes: [...prev.purposes, purpose]
      }));
    } else {
      setCalculatorFormData(prev => ({
        ...prev,
        purposes: prev.purposes.filter(p => p !== purpose)
      }));
    }
  };
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return calculatorFormData.aduType !== "";
      case 2:
        return calculatorFormData.county !== "" && calculatorFormData.city !== "";
      case 3:
        return calculatorFormData.purposes.length > 0;
      case 4:
        return calculatorFormData.designChoice !== "";
      case 5:
        return calculatorFormData.squareFootage !== "" && calculatorFormData.finishLevel !== "";
      case 6:
        return calculatorFormData.firstName !== "" && calculatorFormData.lastName !== "" && calculatorFormData.propertyAddress !== "" && calculatorFormData.email !== "" && calculatorFormData.phone !== "";
      case 7:
        return !isCodeSent || isCodeSent && verificationCode.length === 6;
      default:
        return false;
    }
  };
  const navigate = useNavigate();
  return <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/uda-adu-insider" />
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg overflow-hidden">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>}

      {/* Hero Section */}
      

      {/* Feature Dashboard */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {features.map(feature => <ADUFeatureBox key={feature.id} title={feature.title} icon={feature.icon} description={isMobile ? undefined : feature.description} isActive={activeFeature === feature.id} onClick={() => setActiveFeature(feature.id)} />)}
          </div>

          {/* Feature Content */}
          <AnimatePresence mode="wait">
            <motion.div key={activeFeature} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="bg-white rounded-lg shadow-lg p-8">
              {renderFeatureContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* FAQ Button */}
      <section className="py-4 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Button onClick={() => navigate('/faq')} className="bg-[#e4683b] hover:bg-[#e4683b]/80 text-white text-3xl font-light mx-0 px-[79px] py-[30px]">
            FAQ
          </Button>
        </div>
      </section>

      {/* Main Video Section */}
      <section className="py-12 px-4 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <video className="w-full rounded-lg shadow-lg" autoPlay loop muted playsInline onContextMenu={e => e.preventDefault()} style={{
            pointerEvents: 'none'
          }}>
              <source src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/UDA%20ADU%20MAIN%20PAGE.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvVURBIEFEVSBNQUlOIFBBR0UubXA0IiwiaWF0IjoxNzQ5Njc1OTkzLCJleHAiOjIwNjUwMzU5OTN9.ZqXTGIc_jiNHFo-rqapg8iYsXVKNeOkJPj9lfkW6sjw" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Result Dialog */}
      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">Your ADU Cost Estimate</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Calendly Widget */}
            <div className="w-full">
              <h3 className="text-lg font-semibold mb-4 text-center">Schedule Your Free Consultation</h3>
              <div className="w-full h-[500px] border rounded-lg">
                <iframe src="https://calendly.com/udaadu" width="100%" height="100%" frameBorder="0" title="Schedule Consultation" className="rounded-lg"></iframe>
              </div>
            </div>
            
            {/* Cost Estimate */}
            <div className="text-center space-y-4">
              <div className="bg-[#e4683b]/10 p-6 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Your complete design, build, and material cost for the entire project starts at:</p>
                <p className="text-3xl font-bold text-[#e4683b]">
                  ${calculatedCost.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground mt-3 italic">
                  Your actual cost may vary slightly depending on site conditions and scope — every detail matters. We'll provide an exact, itemized quote during your complimentary ADU meeting.
                </p>
              </div>
              
              <div className="text-sm text-muted-foreground p-4 bg-accent rounded-lg">
                <p>We offer special discounts, grant options, and in-house financing to support our local community. By scheduling and showing up to your appointment with our CEO, you'll automatically receive a 5% discount, with additional benefits discussed based on your situation.</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
      <FloatingADUWizard />
    </div>;
};
export default UDAADUInsider;
